import {
    type AwardBar,
    AwardBarKind,
    type AwardEntry,
    AwardHistoryRange,
    type TopPerformerLensRow,
    TopPerformerLensStatus,
} from "../models";

export const AWARD_CHART_FLOOR_MILLI_USD = 1_000_000;
const IN_PROGRESS_BAR_RATIO = 0.12;

const monthStartUtc = (date: Date, offset = 0): string =>
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offset, 1)).toISOString();

const parseDate = (iso?: string | null): Date | null => {
    const time = iso ? Date.parse(iso) : Number.NaN;
    return Number.isNaN(time) ? null : new Date(time);
};

// An award is paid the month after the one it covers; paidOn is authoritative once processed.
export const payCycleMonthStart = ({
    paidOn,
    awardedMonth,
}: NonNullable<TopPerformerLensRow["award"]>): string | null => {
    const paid = parseDate(paidOn);
    const awarded = parseDate(awardedMonth);
    return paid ? monthStartUtc(paid) : awarded ? monthStartUtc(awarded, 1) : null;
};

export const trailingMonthStarts = (now: Date, count: number): string[] =>
    Array.from({ length: count }, (_, index) => monthStartUtc(now, index - (count - 1)));

export const buildAwardBars = (
    lenses: readonly TopPerformerLensRow[],
    now: Date,
    range: AwardHistoryRange,
): AwardBar[] => {
    const currentMonth = monthStartUtc(now);
    const inProgress = lenses.some((lens) => lens.status === TopPerformerLensStatus.UnderReview);
    const paidAwards = lenses.flatMap(({ lens, award }) => {
        const monthStart = award ? payCycleMonthStart(award) : null;
        return award && monthStart
            ? [{ monthStart, entry: { lensName: lens.name, amountMilliUsd: award.amountMilliUsd } }]
            : [];
    });

    const earliestMonth = new Date(
        paidAwards.reduce(
            (earliest, award) => (award.monthStart < earliest ? award.monthStart : earliest),
            currentMonth,
        ),
    );
    const monthCount =
        range === AwardHistoryRange.Lifetime
            ? (now.getUTCFullYear() - earliestMonth.getUTCFullYear()) * 12 +
              now.getUTCMonth() -
              earliestMonth.getUTCMonth() +
              1
            : range;

    return trailingMonthStarts(now, monthCount).map((monthStart) => {
        const awards: AwardEntry[] = paidAwards
            .filter((paid) => paid.monthStart === monthStart)
            .map((paid) => paid.entry);
        const kind =
            awards.length > 0
                ? AwardBarKind.Awarded
                : inProgress && monthStart === currentMonth
                  ? AwardBarKind.InProgress
                  : AwardBarKind.Empty;
        return {
            kind,
            monthStart,
            amountMilliUsd: awards.reduce((sum, award) => sum + award.amountMilliUsd, 0),
            awards,
        };
    });
};

export interface AwardChartDatum extends AwardBar {
    value: number;
}

export const isAwardChartDatum = (value: unknown): value is AwardChartDatum =>
    typeof value === "object" && value !== null && "kind" in value && "monthStart" in value;

const Y_TICK_INTERVALS = 4;
const Y_TICK_STEP_MILLI_USD = 250_000;

// Even ticks passed explicitly: with a fixed domain recharts appends the raw max as an extra, crowded tick.
export const toAwardChartData = (
    bars: readonly AwardBar[],
): { data: AwardChartDatum[]; max: number; ticks: number[] } => {
    const largest = Math.max(AWARD_CHART_FLOOR_MILLI_USD, ...bars.map((bar) => bar.amountMilliUsd));
    const step = Math.ceil(largest / Y_TICK_INTERVALS / Y_TICK_STEP_MILLI_USD) * Y_TICK_STEP_MILLI_USD;
    const max = step * Y_TICK_INTERVALS;
    return {
        max,
        ticks: Array.from({ length: Y_TICK_INTERVALS + 1 }, (_, index) => index * step),
        data: bars.map((bar) => ({
            ...bar,
            value: bar.kind === AwardBarKind.InProgress ? max * IN_PROGRESS_BAR_RATIO : bar.amountMilliUsd,
        })),
    };
};

const tickFormat = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" });
const labelFormat = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
const wholeUsdFormat = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export const formatAwardMonthTick = (monthStart: string): string => tickFormat.format(new Date(monthStart));

export const formatAwardMonthLabel = (monthStart: string): string => labelFormat.format(new Date(monthStart));

export const formatWholeUsd = (milliUsd: number): string => wholeUsdFormat.format(milliUsd / 1000);
